import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useGetFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useGetFinancialTransaction.ts';
import useUpdateFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useUpdateFinancialTransaction.ts';
import {
  financialTransactionSchema,
  FinancialTransactionFormData,
  FinancialTransactionInputData,
} from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import FinancialTransactionForm from '@/features/dashboard/financialTransactions/components/FinancialTransactionForm.tsx';
import { scrollToAllocationsError } from '@/features/dashboard/financialTransactions/components/allocationsError.ts';
import { financialTransactionRequestDtoMapper } from '@/features/dashboard/financialTransactions/types/FinancialTransactionRequestDto.mapper.ts';
import FinancialTransactionFormSkeleton from '@/features/dashboard/financialTransactions/components/FinancialTransactionFormSkeleton.tsx';
import TagPickerSection from '@/features/dashboard/tags/components/TagPickerSection.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';

interface EditFinancialTransactionModalProps {
  transactionId: string;
  /** Wejście z „Rozdysponuj resztę”: formularz przewija się do przypisań. */
  focusAllocations?: boolean;
  onClose: () => void;
}

const EditFinancialTransactionModal = ({
  transactionId,
  focusAllocations = false,
  onClose,
}: EditFinancialTransactionModalProps) => {
  const { data: transaction, isLoading } =
    useGetFinancialTransaction(transactionId);
  const updateMutation = useUpdateFinancialTransaction(transactionId);

  const methods = useForm<FinancialTransactionInputData>({
    resolver: zodResolver(financialTransactionSchema),
    values: transaction
      ? financialTransactionRequestDtoMapper.fromDtoToForm(transaction)
      : undefined,
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: FinancialTransactionFormData) => {
    updateMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, scrollToAllocationsError)}>
          <ModalHeader title="Edytuj transakcję" onClose={onClose} />
          <ModalContent>
            {isLoading ? (
              <FinancialTransactionFormSkeleton />
            ) : (
              <div className="space-y-4">
                <FinancialTransactionForm
                  mode="edit"
                  focusAllocations={focusAllocations}
                />
                <TagPickerSection
                  targetType={TaggableType.TRANSACTION}
                  targetId={transactionId}
                />
              </div>
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isLoading}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditFinancialTransactionModal;
